// Backtest / research data provider (INTERNAL review tool, not a shipped
// customer feature). Serves our own historical XAU/USD bars so the founder can
// pull a date onto the chart and mark up a studied trader's trades with drawing
// tools, and so the strategy builder can backtest on real 1-minute data. Routed
// ONLY for the "XAUUSD" symbol family (see registry.ts).
//
// The bytes live in an on-disk IndexedDB cache (see candle-cache.ts), NOT in the
// extension package — ship code, not data. The cache fills from OANDA (the
// founder's own broker feed; see oanda-source.ts) — the source of record for
// gold history AND the live tail. For real users this same store swaps its
// source to the backend data hub.
//
// Symbol grammar: "XAUUSD 2024-08-02" = that trading day; "XAUUSD 2024-08-01
// 2024-08-05" = that span; no date = the latest month we have.

import type { DataProvider, CandleResult, ClosesResult } from "./types.js";
import type { OHLCPoint } from "../market-data.js";
import { candleCache, type RawBar, type CandleSource } from "./candle-cache.js";
import {
  getOandaCreds,
  getOandaLiveEnabled,
  oandaGranularity,
  oandaInstrument,
  oandaLiveCandles,
  oandaSource,
  rawBarsToPoints,
} from "./oanda-source.js";

const SYMBOL_PREFIX = "XAUUSD";

/** True for the symbols this provider owns. */
export function isBacktestSymbol(symbol: string): boolean {
  return symbol.trim().toUpperCase().startsWith(SYMBOL_PREFIX);
}

/** Dates embedded in the symbol: one = that day, two = [from, to]. */
function parseSymbol(symbol: string): { from: string | null; to: string | null } {
  const ds = symbol.match(/\d{4}-\d{2}-\d{2}/g) ?? [];
  if (ds.length >= 2) return { from: ds[0]!, to: ds[1]! };
  if (ds.length === 1) return { from: ds[0]!, to: null };
  return { from: null, to: null };
}

// Configure the cache's fill source: OANDA (the founder's broker feed) when a
// token is connected — the source of record for gold history AND the live tail.
// With no token there is no lazy source; reads come from whatever OANDA already
// primed into the cache. Re-resolved when the connection changes (a token may be
// pasted or cleared mid-session).
let fillSource: CandleSource | null = null;
let sourceMode: "none" | "oanda" = "none";
async function ensureSource(): Promise<void> {
  const creds = await getOandaCreds();
  if (creds) {
    if (sourceMode !== "oanda") {
      fillSource = oandaSource(creds);
      candleCache.setSource(fillSource);
      sourceMode = "oanda";
    }
  } else if (sourceMode === "oanda") {
    fillSource = null;
    candleCache.setSource(null);
    sourceMode = "none";
  }
}

/** Append OANDA's live tail (recent candles incl. the still-forming one) to the
 * latest view, so the chart shows a moving bar. Native OANDA intraday
 * granularities share our UTC bucket boundaries, so they seam cleanly onto the
 * cache-resampled series; the forming bucket comes wholly from OANDA. */
async function appendLiveTail(symbol: string, interval: string, points: OHLCPoint[]): Promise<OHLCPoint[]> {
  const creds = await getOandaCreds();
  if (!creds || !(await getOandaLiveEnabled())) return points;
  const instrument = oandaInstrument(symbol);
  const granularity = oandaGranularity(interval);
  if (!instrument || !granularity) return points;
  try {
    const { bars } = await oandaLiveCandles(creds, instrument, granularity, 5);
    if (!bars.length) return points;
    const live = rawBarsToPoints(bars);
    const firstLive = live[0]!.date.getTime();
    return [...points.filter((p) => p.date.getTime() < firstLive), ...live];
  } catch {
    return points; // live is best-effort; never break the historical chart
  }
}

/** The span of months we can show: prefer what's cached, else the source
 * manifest (dev harness) so a fresh cache still opens on real data. */
async function cachedSpan(symbol: string): Promise<{ earliest: string; latest: string } | null> {
  const cached = await candleCache.cachedMonths(symbol);
  if (cached.length) return { earliest: cached[0]!, latest: cached[cached.length - 1]! };
  if (fillSource) {
    const avail = await fillSource.availableMonths(symbol).catch(() => [] as string[]);
    if (avail.length) return { earliest: avail[0]!, latest: avail[avail.length - 1]! };
  }
  return null;
}

/** How many months back the DEFAULT (no date picked) view spans, by timeframe.
 * The survey frames (1h/1d/1w) open on ALL history so you can scroll back
 * through everything; the minute frames open on a bounded recent window so we
 * never load millions of 1-minute bars just to draw. `null` = full history. */
function defaultMonthsBack(interval: string): number | null {
  switch (interval.trim().toLowerCase()) {
    case "1d":
    case "1w":
    case "1wk":
    case "1h":
    case "60m":
      return null; // full history
    case "30m":
    case "15m":
      return 18;
    case "5m":
      return 4;
    default: // 1m
      return 2;
  }
}

/** First day (YYYY-MM-DD) of the month `back` months before `latest`,
 * clamped to `earliest`. */
function monthMinus(latest: string, earliest: string, back: number): string {
  const [y, m] = latest.split("-").map(Number);
  const d = new Date(Date.UTC(y!, m! - 1, 1));
  d.setUTCMonth(d.getUTCMonth() - (back - 1));
  const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  return key < earliest ? earliest : key;
}

function toPoints(bars: RawBar[]): OHLCPoint[] {
  return bars.map((b) => ({
    date: new Date(b.t),
    open: b.o,
    high: b.h,
    low: b.l,
    close: b.c,
    volume: b.v ?? 0,
  }));
}

async function candles(symbol: string, _range: string, interval: string): Promise<CandleResult> {
  await ensureSource();
  const { from, to } = parseSymbol(symbol);

  let lo: number;
  let hi: number;
  if (from && to) {
    lo = Date.parse(`${from}T00:00:00Z`);
    hi = Date.parse(`${to}T23:59:59Z`);
  } else if (from) {
    lo = Date.parse(`${from}T00:00:00Z`);
    hi = Date.parse(`${from}T23:59:59Z`);
  } else {
    const span = await cachedSpan(symbol);
    if (!span) return { candles: [] }; // nothing imported yet and no source
    // hi = end of the latest month (first ms of the following month, minus one).
    const [ly, lm] = span.latest.split("-").map(Number);
    hi = Date.UTC(ly!, lm!, 1) - 1;
    const back = defaultMonthsBack(interval);
    const loStr = back == null ? span.earliest : monthMinus(span.latest, span.earliest, back);
    lo = Date.parse(`${loStr}-01T00:00:00Z`);
  }
  const bars = await candleCache.readCandles(symbol, interval, lo, hi);
  let points = toPoints(bars);
  // Only the latest view (no explicit date range) gets the live forming candle.
  if (!from && !to) points = await appendLiveTail(symbol, interval, points);
  return { candles: points, currentPrice: points.length ? points[points.length - 1]!.close : undefined };
}

export const backtestProvider: DataProvider = {
  id: "backtest",
  displayName: "Backtest data (XAU/USD, internal)",
  assetClasses: ["Forex"],
  realtime: false,
  setup: null,
  candles,
  async closes(symbol, range, interval): Promise<ClosesResult> {
    const { candles: cs, currentPrice } = await candles(symbol, range, interval);
    return { closes: cs.map((c) => c.close), regularMarketPrice: currentPrice };
  },
};
